/**
 * Industry-standard fields for Buy & Sell and Rentals posting flows.
 * Includes online / digital-asset handoff options shared with other apps.
 */

export const SALE_LISTING_TYPE_OPTIONS = [
  { value: 'for_sale', label: 'For sale' },
  { value: 'wanted', label: 'Wanted / looking to buy' },
  { value: 'service', label: 'Service offering' },
  { value: 'digital', label: 'Digital goods / license' },
  { value: 'giveaway', label: 'Free / giveaway' },
];

export const SALE_CATEGORY_OPTIONS = [
  { id: 'electronics', label: 'Electronics' },
  { id: 'phones', label: 'Phones / tablets' },
  { id: 'computers', label: 'Computers / IT' },
  { id: 'appliances', label: 'Appliances' },
  { id: 'furniture', label: 'Furniture' },
  { id: 'home_garden', label: 'Home & garden' },
  { id: 'vehicles', label: 'Vehicles / parts' },
  { id: 'fashion', label: 'Fashion / accessories' },
  { id: 'baby_kids', label: 'Baby / kids' },
  { id: 'sports', label: 'Sports / outdoors' },
  { id: 'tools', label: 'Tools / hardware' },
  { id: 'farm', label: 'Farm / livestock supplies' },
  { id: 'business', label: 'Business / office' },
  { id: 'tickets', label: 'Tickets / vouchers' },
  { id: 'digital_goods', label: 'Digital goods / files' },
  { id: 'software', label: 'Software / licenses' },
  { id: 'gift_cards', label: 'Gift cards / top-ups' },
  { id: 'crypto_assets', label: 'Crypto / digital assets' },
  { id: 'services', label: 'Services' },
  { id: 'other', label: 'Other' },
];

export const SALE_CONDITION_OPTIONS = [
  { value: 'new', label: 'New / sealed' },
  { value: 'like_new', label: 'Like new' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'for_parts', label: 'For parts / not working' },
  { value: 'na', label: 'N/A (service / digital)' },
];

export const SALE_FULFILLMENT_OPTIONS = [
  { id: 'pickup', label: 'Buyer pickup' },
  { id: 'meetup', label: 'Public meetup' },
  { id: 'delivery', label: 'Seller can deliver' },
  { id: 'courier', label: 'Courier / runner OK' },
  { id: 'shipping', label: 'Ship / post' },
  { id: 'digital_delivery', label: 'Digital delivery (link / key / file)' },
  { id: 'online_transfer', label: 'Online funds / wallet transfer' },
  { id: 'escrow_online', label: 'Online escrow lock → release' },
];

export const SALE_PRICE_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed price' },
  { value: 'negotiable', label: 'Negotiable' },
  { value: 'or_best', label: 'Or best offer (OBO)' },
  { value: 'free', label: 'Free' },
];

export const SALE_CURRENCY_OPTIONS = [
  { value: 'JMD', label: 'JMD' },
  { value: 'USD', label: 'USD' },
  { value: 'USDC', label: 'USDC' },
  { value: 'USDT', label: 'USDT' },
  { value: 'BTC', label: 'BTC' },
  { value: 'ETH', label: 'ETH' },
  { value: 'other_crypto', label: 'Other crypto' },
  { value: 'other', label: 'Other' },
];

export const RENTAL_ASSET_TYPE_OPTIONS = [
  { value: 'short_term', label: 'Place to stay' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'equipment', label: 'Equipment / tools' },
  { value: 'fashion', label: 'Fashion / event wear' },
  { value: 'event_space', label: 'Event space / venue' },
  { value: 'electronics', label: 'Electronics / AV' },
  { value: 'party', label: 'Party / event gear' },
  { value: 'digital', label: 'Digital asset / software license' },
  { value: 'online_access', label: 'Online access / account seat' },
  { value: 'other', label: 'Other' },
];

export const RENTAL_RATE_UNIT_OPTIONS = [
  { value: 'hour', label: 'Per hour' },
  { value: 'day', label: 'Per day' },
  { value: 'night', label: 'Per night' },
  { value: 'week', label: 'Per week' },
  { value: 'month', label: 'Per month' },
  { value: 'event', label: 'Per event / flat' },
];

export const RENTAL_INCLUDED_OPTIONS = [
  { id: 'delivery', label: 'Delivery / drop-off' },
  { id: 'pickup', label: 'Pickup at end' },
  { id: 'setup', label: 'Setup / install' },
  { id: 'operator', label: 'Operator / driver included' },
  { id: 'insurance', label: 'Basic insurance / cover' },
  { id: 'fuel', label: 'Fuel / power included' },
  { id: 'cleaning', label: 'Cleaning included' },
  { id: 'wifi', label: 'Wi‑Fi' },
  { id: 'parking', label: 'Parking' },
  { id: 'remote_access', label: 'Remote / online access' },
  { id: 'digital_delivery', label: 'Digital keys / license delivery' },
];

export const RENTAL_REQUIREMENTS_OPTIONS = [
  { id: 'id', label: 'Government ID' },
  { id: 'license', label: 'Valid driver’s licence' },
  { id: 'deposit', label: 'Refundable deposit' },
  { id: 'reference', label: 'Reference / guarantor' },
  { id: 'age', label: 'Minimum age applies' },
  { id: 'clean_record', label: 'Clean driving record' },
  { id: 'wallet', label: 'Wallet / escrow capable' },
];

export const RENTAL_CANCEL_OPTIONS = [
  { value: 'flexible', label: 'Flexible — free cancel until start' },
  { value: 'moderate', label: 'Moderate — 24–48h notice' },
  { value: 'strict', label: 'Strict — deposit may be kept' },
  { value: 'non_refundable', label: 'Non-refundable' },
];

export const RENTAL_BOOKING_OPTIONS = [
  { value: 'request', label: 'Request to book (I approve)' },
  { value: 'instant', label: 'Instant book if requirements met' },
];

export const LISTING_TERMS_SUMMARY =
  'I confirm this listing is accurate, legal to sell or rent, and does not involve ' +
  'prohibited, hazardous, or stolen goods (including illicit digital assets). I own or am ' +
  'authorized to offer this item/space/license, and I agree to the marketplace terms for ' +
  'Buy & Sell and Rentals — including online and escrow settlement when selected.';

export default {
  SALE_CATEGORY_OPTIONS,
  RENTAL_ASSET_TYPE_OPTIONS,
  LISTING_TERMS_SUMMARY,
};
